import { clsx } from "clsx";
import { useEffect, useRef, useState } from "react";
import { Flex } from "@/components/Flex/Flex";
import { Icon } from "@/components/Icon";
import { Stack } from "@/components/Stack/Stack";
import { Text } from "@/components/Text";
import { type CheckoutParams, useCheckout } from "@/context/CheckoutContext";
import { syncDialogModal } from "@/utils/safe-dialog";
import { trackCheckoutClose, trackCheckoutOpen } from "@/utils/tracking";
import { CheckoutForm } from "./CheckoutForm";
import { CheckoutSalesClosed, CheckoutSuccess } from "./CheckoutSuccess";
import "./checkout-controls.css";

export function CheckoutDrawer() {
  const { state, params, error, paymentUrl, close, submit } = useCheckout();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const isVisible = state !== "idle";
  const [displayParams, setDisplayParams] = useState<CheckoutParams | null>(params);

  useEffect(() => {
    if (params) setDisplayParams(params);
  }, [params]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    syncDialogModal(dialog, isVisible);
  }, [isVisible]);

  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isVisible]);

  useEffect(() => {
    if (isVisible && params) {
      trackCheckoutOpen(params.productTitle);
    }
  }, [isVisible, params]);

  useEffect(() => {
    if (state === "error" || state === "sales-closed" || state === "success") {
      contentRef.current?.scrollTo({ top: 0 });
    }
  }, [state]);

  const current = params ?? displayParams;
  if (!current) return null;

  const isLoading = state === "submitting";
  const isSuccess = state === "success";
  const showError = state === "error";
  const isSalesClosed = state === "sales-closed";

  return (
    // The className below carries a raw z-index utility (value 50) — fallback-only
    // debt: a native <dialog> renders in the browser's top layer and ignores z-index
    // entirely, so the value is inert on every browser that supports showModal().
    // utils/safe-dialog.ts falls back to setAttribute("open") on old Safari / Opera
    // Mini / Android WebViews, where that utility becomes live and ranks this drawer
    // BELOW the overlay (90) and modal (100) tiers. Leaving the value documents that
    // gap without changing behaviour for a population no visual gate can exercise; do
    // not tokenize it (docs/specs/website/design-token-linting.md).
    <dialog
      ref={dialogRef}
      data-component="CheckoutDrawer"
      className="drawer-dialog fixed inset-0 z-50 m-0 h-full max-h-full w-full max-w-full bg-transparent p-0"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          trackCheckoutClose();
          close();
        }
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          trackCheckoutClose();
          close();
        }
      }}
    >
      <button
        type="button"
        className="drawer-backdrop fixed inset-0 bg-fg/45"
        aria-label="Закрыть оформление заказа"
        onClick={() => {
          trackCheckoutClose();
          close();
        }}
      />

      <Stack
        className={clsx(
          "drawer-panel fixed overflow-hidden bg-surface shadow-xl",
          "max-lg:inset-x-0 max-lg:bottom-0 max-lg:max-h-[85vh] max-lg:rounded-t-[var(--corner-xl)] max-lg:shadow-[0_-16px_48px_color-mix(in_oklab,var(--raw-ink)_18%,transparent)]",
          "lg:inset-y-0 lg:right-0 lg:w-105 lg:rounded-l-[var(--corner-xl)] lg:shadow-[-24px_0_64px_color-mix(in_oklab,var(--raw-ink)_18%,transparent)]",
        )}
      >
        <Flex align="center" justify="center" className="h-8 shrink-0 lg:hidden">
          <span className="h-[5px] w-12 rounded-full bg-border" aria-hidden="true" />
        </Flex>

        <Stack as="header" gap={16} className="shrink-0 px-6 pt-2 pb-6 lg:px-10 lg:pt-9">
          <Flex align="center" justify="between" gap={16} className="w-full">
            <Text
              as="span"
              variant="eyebrow"
              className="inline-flex h-7 items-center rounded-3xl bg-surface-brand px-3 text-brand uppercase"
            >
              Оплата
            </Text>
            <Flex
              as="button"
              type="button"
              onClick={() => {
                trackCheckoutClose();
                close();
              }}
              align="center"
              justify="center"
              className="size-10 shrink-0 rounded-full bg-surface-brand text-brand-deep hover:bg-border-subtle disabled:opacity-50"
              aria-label="Закрыть"
              disabled={isLoading}
            >
              <Icon name="x-mark" size="sm" />
            </Flex>
          </Flex>
          <Stack gap={8}>
            <Text as="h3" variant="heading" className="text-fg">
              {current.productTitle}
            </Text>
            {current.productSubtitle && (
              <Text as="p" variant="body-sm" className="text-muted">
                {current.productSubtitle}
              </Text>
            )}
            <Text as="p" variant="heading" className="text-brand">
              {current.productPrice}
            </Text>
          </Stack>
        </Stack>

        <div className="h-px shrink-0 bg-border" />

        <div ref={contentRef} className="min-h-0 flex-1 overflow-y-auto px-6 py-6 lg:px-10">
          {isSuccess ? (
            <CheckoutSuccess paymentUrl={paymentUrl} free={current.free} />
          ) : isSalesClosed ? (
            <CheckoutSalesClosed onClose={close} />
          ) : (
            <>
              {showError && (
                <div role="alert" className="ck-alert mb-4">
                  <Icon name="exclamation-circle" size="sm" />
                  <Text as="span" variant="body-sm">
                    {error}
                  </Text>
                </div>
              )}
              <CheckoutForm
                onSubmit={submit}
                isLoading={isLoading}
                offerId={String(current.offerId)}
                tier={current.productTitle}
                ofertaUrl={current.ofertaUrl || "https://lenacareer.school/oferta"}
                ofertaProductName={current.ofertaProductName || current.productTitle}
                free={current.free}
              />
            </>
          )}
        </div>
      </Stack>
    </dialog>
  );
}

CheckoutDrawer.displayName = "CheckoutDrawer";
